import FormInput from '../../../components/FormInput';
import FormEntry from '../../../components/FormEntry';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import SectionHeader from 'components/SectionHeader';
import { useConfigsId } from '../../../hooks/useConfigs';
import { getSingletonConfigCalibrationId } from '../../../hooks/useSingletonSessions';
import { CalibrationRefType, SessionAnalysisTypeUtils, SessionAnalysisType } from '../../../types/SessionAnalysisType';
import { useSessions, useSessionCalcData } from '../../../hooks/useSessions';
import { useChainModels } from '../../../hooks/useChainModels';

type SelectionMode = 'team' | 'session';

export default function NewSessionCalibrationConfig(props: {
  ridersById: Map<any, any>;
  onRiderWarningChange?: (show: boolean) => void;
}) {
  const { ridersById } = props;
  const { watch, setValue, register } = useFormContext();
  const [selectionMode, setSelectionMode] = useState<SelectionMode>('team');
  const { data: chainModels } = useChainModels();

  // Watch for analysis_type to determine if indoor session
  const analysisType = watch('analysis_type');
  const isIndoorSession = Number(analysisType) >= SessionAnalysisType.TRAVERSE_INDOORS;

  // For Team Calibration mode
  const [teamCalibrationConfig, setTeamCalibrationConfig] = useState<any>(null);

  // For Session/Config mode
  const [selectedSessionId, setSelectedSessionId] = useState<string>('');
  const [availableConfigs, setAvailableConfigs] = useState<any[]>([]);

  // Fetch data
  const { data: configs } = useConfigsId();
  const { data: sessions } = useSessions();
  // Only fetch session calc data if we have a valid selected session ID
  // Pass a dummy ID if none selected to prevent API call with empty ID
  const { data: selectedSessionCalcData } = useSessionCalcData(selectedSessionId || 'no-session-selected');

  // Rider mass and suggested combined mass
  const EQUIPMENT_MASS = 8.5;
  const watchedRiderId = watch('rider_id');
  const riderMass = watchedRiderId
    ? ridersById.get(watchedRiderId)?.mass || 0
    : 0;
  const suggestedMass = riderMass + EQUIPMENT_MASS;

  // Set default front_tooth_count on mount
  useEffect(() => {
    setValue('front_tooth_count', 56);
  }, [setValue]);

  // Update combined_mass when rider selection changes
  useEffect(() => {
    if (riderMass > 0) {
      setValue(
        'combined_mass',
        riderMass + EQUIPMENT_MASS,
        { shouldValidate: true },
      );
    }
  }, [riderMass, setValue]);

  // Handle mode change
  const handleModeChange = (mode: SelectionMode) => {
    setSelectionMode(mode);
    // Clear form values when switching modes
    setValue('calibration_id', '', { shouldValidate: false });
    setValue('combined_mass', '', { shouldValidate: false });
    setValue('crr', '', { shouldValidate: false });
    setValue('source_session_id', '', { shouldValidate: false }); // Clear the session dropdown
    setSelectedSessionId('');
    setAvailableConfigs([]);
  };

  // Fetch team calibration config when in team mode
  useEffect(() => {
    if (selectionMode !== 'team') return;

    const fetchTeamCalibration = async () => {
      try {
        const teamCalibrationId = await getSingletonConfigCalibrationId(CalibrationRefType.TEAM);

        // Team calibration loaded: teamCalibrationId
        if (teamCalibrationId && configs) {
          const teamConfig = configs.find((c) => c.id === teamCalibrationId);
          if (teamConfig) {
            setTeamCalibrationConfig(teamConfig);
            // Automatically set the calibration_id and related fields
            setValue('calibration_id', teamConfig.id, {
              shouldValidate: true,
            });
            const mass = riderMass > 0
              ? riderMass + EQUIPMENT_MASS
              : teamConfig.combined_mass;
            setValue('combined_mass', mass, {
              shouldValidate: true,
            });
            setValue('crr', teamConfig.crr, {
              shouldValidate: true,
            });
            const ftc =
              teamConfig.drivetrain?.front_tooth_count;
            if (ftc) {
              setValue('front_tooth_count', ftc);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching team calibration:', error);
      }
    };

    if (configs && configs.length > 0) {
      fetchTeamCalibration();
    }
  }, [configs, setValue, selectionMode]);

  // Watch for session selection changes
  useEffect(() => {
    if (selectionMode !== 'session') return;

    const subscription = watch((value, { name }) => {
      if (name === 'source_session_id' && value.source_session_id) {
        // Session field changed: value.source_session_id
        setSelectedSessionId(value.source_session_id);
      }
    });

    return () => subscription.unsubscribe();
  }, [watch, selectionMode]);

  // Update available configs when session is selected (for session mode)
  useEffect(() => {
    if (selectionMode !== 'session') return;

    if (selectedSessionId && selectedSessionId !== 'no-session-selected') {
      // Session selected: selectedSessionId
      // Session data received: selectedSessionCalcData

      if (selectedSessionCalcData && selectedSessionCalcData.configs) {
        // Configs found: selectedSessionCalcData.configs.length configs
        setAvailableConfigs(selectedSessionCalcData.configs);
      } else {
        // No configs found for session: selectedSessionId
        setAvailableConfigs([]);
      }
    }

    // Clear calibration selection when session changes
    if (selectedSessionId) {
      setValue('calibration_id', '', { shouldValidate: false });
      setValue('combined_mass', '', { shouldValidate: false });
      setValue('crr', '', { shouldValidate: false });
    }
  }, [selectedSessionCalcData, selectedSessionId, setValue, selectionMode]);

  // Watch for changes in calibration selection (for session mode)
  useEffect(() => {
    if (selectionMode !== 'session') return;

    const subscription = watch((value: any, info: any) => {
      if (info.name === 'calibration_id' && value.calibration_id) {
        const selectedCal = availableConfigs.find(
          (c) => c.id === value.calibration_id,
        );
        if (selectedCal) {
          const mass = riderMass > 0
            ? riderMass + EQUIPMENT_MASS
            : selectedCal.combined_mass;
          setValue('combined_mass', mass, {
            shouldValidate: true,
          });
          setValue('crr', selectedCal.crr, {
            shouldValidate: true,
          });
          const ftc =
            selectedCal.drivetrain?.front_tooth_count;
          if (ftc) {
            setValue('front_tooth_count', ftc);
          }
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, setValue, availableConfigs, selectionMode]);

  return (
    <div className="cr-mb-4 cr-flex cr-w-full cr-flex-[1_1] cr-flex-col">
      <div className="cr-h-full cr-w-full">
        <div className="cr-flex cr-flex-col">
          <SectionHeader title="Calibration Configuration" />

          <div className="cr-flex cr-flex-col cr-rounded-lg cr-bg-white cr-p-2 cr-text-center cr-shadow-md md:cr-w-[640px] md:cr-text-left">
            {/* Radio buttons for selection mode */}
            <FormEntry label="Copy Configuration From">
              <div className="cr-flex cr-flex-col cr-gap-2">
                <label className="cr-flex cr-cursor-pointer cr-items-center">
                  <input
                    type="radio"
                    name="selectionMode"
                    value="team"
                    checked={selectionMode === 'team'}
                    onChange={() => handleModeChange('team')}
                    className="cr-mr-2"
                  />
                  <span>Default Team Calibration</span>
                </label>
                <label className="cr-flex cr-cursor-pointer cr-items-center">
                  <input
                    type="radio"
                    name="selectionMode"
                    value="session"
                    checked={selectionMode === 'session'}
                    onChange={() => handleModeChange('session')}
                    className="cr-mr-2"
                  />
                  <span>Select from Session</span>
                </label>
              </div>
            </FormEntry>

            {/* Team Calibration Mode */}
            {selectionMode === 'team' && (
              <FormEntry label="Calibration">
                <div className="cr-flex cr-items-center cr-rounded-md cr-border cr-border-gray-300 cr-bg-gray-50 cr-px-3 cr-py-2">
                  <span className="cr-text-gray-700">
                    {teamCalibrationConfig
                      ? `Team Calibration: ${teamCalibrationConfig.description} ` +
                        `(${teamCalibrationConfig.combined_mass} kg, CRR: ${teamCalibrationConfig.crr})`
                      : 'Loading Team Calibration...'}
                  </span>
                </div>
                {/* Hidden input to maintain form validation */}
                <input type="hidden" {...register('calibration_id', { required: 'Calibration is Required' })} />
              </FormEntry>
            )}

            {/* Session/Config Mode */}
            {selectionMode === 'session' && (
              <>
                <FormEntry label="Select Session">
                  <FormInput
                    fieldName="source_session_id"
                    options={Array.from(sessions || [])
                      .filter((session) => session.analysis_type >= SessionAnalysisTypeUtils.singletonLevel())
                      .reverse()}
                    optionLabelFn={(session) => `${session.date} - ${session.description}`}
                    emptyOption="Select a Session"
                    customClass="cr-w-full md:cr-w-128"
                    required="Session is Required"
                  />
                </FormEntry>

                {selectedSessionId && (
                  <FormEntry label="Select Configuration">
                    <FormInput
                      fieldName="calibration_id"
                      options={availableConfigs}
                      optionLabelFn={(config) =>
                        `${config.letter} - ${config.description} (${config.combined_mass} kg, CRR: ${config.crr})`
                      }
                      emptyOption="Select a Configuration"
                      customClass="cr-w-full md:cr-w-128"
                      required="Configuration is Required"
                    />
                  </FormEntry>
                )}
              </>
            )}

            {/* Common fields */}
            <FormEntry label="Combined Mass">
              <div className="cr-flex cr-flex-col cr-gap-1">
                {riderMass > 0 && (
                  <>
                    <div className="cr-flex cr-gap-6 cr-text-sm">
                      <span className="cr-font-semibold">
                        Total
                      </span>
                      <span className="cr-font-semibold">
                        Rider
                      </span>
                      <span className="cr-font-semibold">
                        Bike &amp; Equip.
                      </span>
                    </div>
                    <div className="cr-text-sm cr-text-gray-600">
                      {suggestedMass.toFixed(1)}
                      {' (suggested) = '}
                      {riderMass} + {EQUIPMENT_MASS}
                    </div>
                  </>
                )}
                <div className="cr-flex cr-items-center cr-gap-2">
                  <FormInput
                    fieldName="combined_mass"
                    required="Combined Mass is Required"
                    inputType="number"
                    customClass="cr-w-24"
                  />
                  <span className="cr-text-sm">kg</span>
                  <span className="cr-text-sm cr-font-semibold">
                    Actual
                  </span>
                </div>
              </div>
            </FormEntry>
            <FormEntry label="CRR">
              <FormInput fieldName="crr" inputType="number" />
            </FormEntry>

            {/* Speed Sensor Location - for all session types */}
            <FormEntry label="Speed Sensor Location">
              <div className="cr-flex cr-flex-row cr-gap-4">
                <label className="cr-flex cr-cursor-pointer cr-items-center">
                  <input
                    type="radio"
                    value="1"
                    defaultChecked
                    {...register('speed_sensor_location')}
                    className="cr-mr-2"
                  />
                  <span>Front</span>
                </label>
                <label className="cr-flex cr-cursor-pointer cr-items-center">
                  <input type="radio" value="0" {...register('speed_sensor_location')} className="cr-mr-2" />
                  <span>Rear</span>
                </label>
                <label className="cr-flex cr-cursor-pointer cr-items-center">
                  <input type="radio" value="-1" {...register('speed_sensor_location')} className="cr-mr-2" />
                  <span>None</span>
                </label>
              </div>
            </FormEntry>

            {/* Indoor Session Parameters */}
            {isIndoorSession && (
              <>
                <FormEntry label="Wheel Circumference (m)">
                  <FormInput
                    fieldName="wheel_circumference"
                    inputType="number"
                    defaultValue={2.1}
                  />
                </FormEntry>
              </>
            )}

            {/* Drivetrain / Chain Drag Model */}
            <FormEntry label="Chain Drag Model">
              <div className="cr-flex cr-flex-col cr-gap-2">
                <div
                  className="cr-flex cr-items-center cr-gap-2"
                >
                  <span className="cr-text-sm cr-text-gray-600">
                    Model:
                  </span>
                  <select
                    {...register('model_chain_id')}
                    className={
                      'cr-rounded cr-border cr-border-gray-300'
                      + ' cr-px-3 cr-py-1 cr-text-sm'
                      + ' cr-text-blue-900'
                    }
                  >
                    <option value="">Select a Model</option>
                    {chainModels?.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name}
                        {m.is_default ? ' (Default)' : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div
                  className="cr-flex cr-items-center cr-gap-2"
                >
                  <FormInput
                    fieldName="front_tooth_count"
                    inputType="number"
                    customClass="cr-w-24"
                  />
                  <span className="cr-text-sm">
                    teeth (front chainring)
                  </span>
                </div>
              </div>
            </FormEntry>
          </div>
        </div>
      </div>
    </div>
  );
}
